import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { visuallyHidden } from "@mui/utils";
import _ from "lodash";
import FilterAutocomplete from "marketplace/_views/filters/filter-autocomplete";
import {
  FilterChipValueType,
  ArrayChip,
  RangeChip,
  TextChip,
  RatingChip,
} from "marketplace/_views/filters/filter-chip";
import { FilterChips, FilterChipType } from "marketplace/_views/filters/filter-chips";
import FilterNumeric from "marketplace/_views/filters/filter-numeric";
import FilterRating from "marketplace/_views/filters/filter-rating";
import {
  countActiveFilters,
  FilterSectionGroup,
  groupFiltersIntoSections,
} from "marketplace/_views/filters/filter-sections";
import FilterSlider from "marketplace/_views/filters/filter-slider";
import FilterTextField from "marketplace/_views/filters/filter-textfield";
import { isEntityTypeInclusionFilterName } from "marketplace/search/entity-type-inclusion-filters";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";

export enum ControlType {
  Autocomplete = "autocomplete",
  Slider = "slider",
  Switch = "switch",
  Text = "text",
  Rating = "rating",
  Numeric = "numeric",
}

/**
 * Whether a control's value sets no filter. A number filter's default is zero, and a range at its
 * full extent is every rate there is; neither narrows anything, so neither is a filter to apply.
 */
function isUnsetValue(filter: FilterChipType, value: FilterChipValueType | undefined): boolean {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value)) {
    if (value.length === 0) return true;
    return filter instanceof RangeChip && _.isEqual(value.map(Number), getFullRange(filter));
  }
  return typeof filter.default === "number" && Number(value) === filter.default;
}

/** A range filter's whole extent: its default, or its bounds when it was given no default. */
function getFullRange(filter: RangeChip): number[] {
  if (Array.isArray(filter.default) && filter.default.length === 2) return filter.default.map(Number);
  return [filter.min ?? 0, filter.max ?? 100];
}

type FilterListProps = {
  filters: FilterChips;
  dialog: boolean;
  /** Leaves out the list's own "Filters" heading, for a surface whose title bar already says it. */
  hideTitle?: boolean;
  searchParams?: URLSearchParams;
  onChange: (filter: FilterChipType) => void;
  onAdded: (filter: FilterChipType) => void;
  onRemoved: (filter: FilterChipType) => void;
};

export default function FilterList(props: Readonly<FilterListProps>) {
  const { filters, dialog, hideTitle, searchParams, onChange, onAdded, onRemoved } = props;

  const didInitFromQueryRef = useRef(false);

  const toggleFilter = useCallback(
    (filterName: string, enabled: boolean) => {
      let updatedFilters = [...filters.chips];
      let index = updatedFilters.findIndex((f) => f.filterName === filterName);
      if (index < 0) return;

      updatedFilters[index].enabled = enabled;

      onChange(updatedFilters[index]);
    },
    [filters.chips, onChange],
  );

  const updateFilterValue = useCallback(
    (filterName: string, value: FilterChipValueType): FilterChipType | undefined => {
      let updatedFilters = [...filters.chips];
      let index = updatedFilters.findIndex((f) => f.filterName === filterName);
      if (index < 0) return;

      updatedFilters[index].value = value;
      onChange(updatedFilters[index]);
      return updatedFilters[index];
    },
    [filters.chips, onChange],
  );

  const updateFilterState = useCallback(
    (filterName: string, value: FilterChipValueType, enabled: boolean): FilterChipType | undefined => {
      const updatedFilter = filters.chips.find((filter) => filter.filterName === filterName);
      if (!updatedFilter) return;

      updatedFilter.value = value;
      updatedFilter.enabled = enabled;
      onChange(updatedFilter);
      return updatedFilter;
    },
    [filters.chips, onChange],
  );

  const initFromQueryString = useCallback(() => {
    if (!searchParams) return;

    for (let [name, value] of searchParams.entries()) {
      let filter: FilterChipType | undefined = filters.chips.find((f) => f.filterName === name);
      if (!filter) continue;

      let parsedValue: FilterChipValueType = value;
      let enabled = true;

      if (Array.isArray(filter.default)) {
        parsedValue = value.split(",");
      } else if (typeof filter.default === "boolean") {
        parsedValue = value === "true";
        enabled = parsedValue;
      } else if (typeof filter.default === "number") {
        parsedValue = Number(value);
      }

      updateFilterState(name, parsedValue, enabled);
    }
  }, [filters.chips, searchParams, updateFilterState]);

  useEffect(() => {
    if (!searchParams) return;
    if (didInitFromQueryRef.current) return;

    didInitFromQueryRef.current = true;
    initFromQueryString();
  }, [searchParams, initFromQueryString]);

  const checkIfFilterDisabled = useCallback(
    (filters: FilterChips) => {
      for (const prevChip of filters.chips) {
        const currentChip = filters.chips.find((chip) => chip.filterName === prevChip.filterName);

        if (currentChip?.enabled !== prevChip.enabled && currentChip?.enabled === false) {
          toggleFilter(currentChip.filterName, false);
          updateFilterValue(currentChip.filterName, currentChip.default);
          onRemoved(currentChip);
        }
      }
    },
    [onRemoved, toggleFilter, updateFilterValue],
  );

  useEffect(() => {
    checkIfFilterDisabled(filters);
  }, [checkIfFilterDisabled, filters]);

  /**
   * A control reported a value. There is no switch to reveal a control any more, so a value is what
   * turns a filter on, and emptying the control is what turns it off.
   */
  function applyValue(filter: FilterChipType, value: FilterChipValueType) {
    if (isUnsetValue(filter, value)) {
      if (filter.enabled) onRemoved(filter);
      return;
    }
    if (!filter.enabled) onAdded(filter);
    updateFilterValue(filter.filterName, value);
  }

  // Switches and checkboxes are on/off filters: the profile types, and the yes-or-no questions.
  function toggleOnOffFilter(filter: FilterChipType, checked: boolean) {
    const updatedFilter = updateFilterState(filter.filterName, checked, checked) ?? filter;
    if (checked) {
      onAdded(updatedFilter);
    } else {
      onRemoved(updatedFilter);
    }
  }

  /**
   * The control for one filter. A control that holds a draft is keyed on the applied value, so it
   * starts again from that value when the filter changes somewhere else - its chip removed, Clear
   * all, a filter read from a sentence - instead of keeping text nobody is searching for.
   */
  function getControl(filter: FilterChipType, disabled: boolean) {
    const onChange = (_filterName: string, value: FilterChipValueType) => applyValue(filter, value);

    switch (filter.controlType) {
      case ControlType.Autocomplete: {
        const autocompleteFilter = filter as ArrayChip;
        // Chips hold their value as a list whatever the control is, but a single-choice
        // autocomplete takes one value. Handing it the list made Material read the array itself as
        // the chosen option, which it reported on every render of a state or billing-unit filter.
        const chosen = autocompleteFilter.enabled && Array.isArray(autocompleteFilter.value) ? autocompleteFilter.value : [];
        const multiple = autocompleteFilter.multiple ?? false;
        return (
          <FilterAutocomplete
            filterName={autocompleteFilter.filterName}
            placeholder={autocompleteFilter.title}
            value={multiple ? chosen : (chosen[0] ?? null)}
            onChange={onChange}
            options={autocompleteFilter.options}
            freeSolo={autocompleteFilter.freeSolo ?? false}
            multiple={multiple}
            disabled={disabled}
          />
        );
      }
      case ControlType.Slider: {
        const rangeFilter = filter as RangeChip;
        const applied =
          rangeFilter.enabled && Array.isArray(rangeFilter.value) && rangeFilter.value.length === 2
            ? rangeFilter.value
            : getFullRange(rangeFilter);
        const value = applied.map(Number);
        return (
          <FilterSlider
            key={`${rangeFilter.filterName}:${String(value)}`}
            filterName={rangeFilter.filterName}
            label={rangeFilter.title}
            value={value}
            min={rangeFilter.min}
            max={rangeFilter.max}
            step={rangeFilter.steps}
            disabled={disabled}
            onChange={onChange}
          />
        );
      }
      case ControlType.Text: {
        const textFilter = filter as TextChip;
        const value = textFilter.enabled ? (textFilter.value?.toString() ?? "") : "";
        return (
          <FilterTextField
            key={`${textFilter.filterName}:${value}`}
            filterName={textFilter.filterName}
            placeholder={textFilter.title}
            value={value}
            disabled={disabled}
            onChange={onChange}
          />
        );
      }
      case ControlType.Rating: {
        const ratingFilter = filter as RatingChip;
        return (
          <FilterRating
            filterName={ratingFilter.filterName}
            value={ratingFilter.enabled ? Number(ratingFilter.value) : 0}
            precision={ratingFilter.precision ?? 1}
            disabled={disabled}
            onChange={onChange}
          />
        );
      }
      case ControlType.Numeric: {
        const applied = filter.enabled ? Number(filter.value) : 0;
        const value = applied > 0 ? applied : null;
        return (
          <FilterNumeric
            key={`${filter.filterName}:${String(value)}`}
            filterName={filter.filterName}
            label={filter.title}
            value={value}
            disabled={disabled}
            onChange={onChange}
          />
        );
      }
      default:
        return null;
    }
  }

  function renderFilter(filter: FilterChipType, disabled: boolean) {
    const dense = !dialog;
    if (isEntityTypeInclusionFilterName(filter.filterName)) {
      return (
        <FilterRow key={filter.filterName} dense={dense}>
          <FilterTitle primary={filter.title} slotProps={{ primary: { variant: dense ? "body2" : "body1" } }} />
          <Switch
            size={dense ? "small" : "medium"}
            checked={filter.enabled}
            color="primary"
            slotProps={{ input: { "aria-label": filter.title } }}
            onChange={(event) => toggleOnOffFilter(filter, event.target.checked)}
          />
        </FilterRow>
      );
    }
    if (filter.controlType === ControlType.Switch) {
      return (
        <CheckboxRow key={filter.filterName} dense={dense}>
          <FormControlLabel
            label={filter.title}
            disabled={disabled}
            slotProps={{ typography: { variant: dense ? "body2" : "body1" } }}
            control={
              <Checkbox
                size={dense ? "small" : "medium"}
                checked={filter.enabled}
                onChange={(event) => toggleOnOffFilter(filter, event.target.checked)}
              />
            }
          />
        </CheckboxRow>
      );
    }
    return (
      <ControlRow key={filter.filterName} dense={dense}>
        {getControl(filter, disabled)}
      </ControlRow>
    );
  }

  const groups = groupFiltersIntoSections(filters?.chips ?? []);

  return (
    <FilterContainer dialog={dialog} elevation={0}>
      {!hideTitle && <Title variant="subtitle1">Filters</Title>}
      {groups.map((group) => {
        // A section for one kind of profile is greyed out while that kind is left out of the
        // results: its filters could only narrow profiles the searcher has already hidden.
        const appliesTo = group.section.appliesTo;
        const excluded = Boolean(appliesTo && filters.findByName(appliesTo.filterName)?.enabled === false);
        return (
          <FilterSectionView
            key={group.section.id}
            group={group}
            excluded={excluded}
            activeCount={countActiveFilters(group.filters)}
          >
            {group.filters.map((filter) => renderFilter(filter, excluded))}
          </FilterSectionView>
        );
      })}
    </FilterContainer>
  );
}

type FilterSectionViewProps = {
  group: FilterSectionGroup;
  excluded: boolean;
  activeCount: number;
  children: React.ReactNode;
};

/**
 * One section of the sidebar. The profile types are always open; every other section folds, opens
 * on its own when something in it is set, and says in its header how many of its filters are.
 */
function FilterSectionView(props: Readonly<FilterSectionViewProps>) {
  const { group, excluded, activeCount, children } = props;
  const { section } = group;
  const contentId = useId();
  const [open, setOpen] = useState<boolean>(!section.collapsible || section.openByDefault || activeCount > 0);

  const appliesTo = section.appliesTo && (
    <AppliesTo>{excluded ? `${section.appliesTo.label} are hidden` : `${section.appliesTo.label} only`}</AppliesTo>
  );

  return (
    <Section data-filter-section={section.id}>
      {section.collapsible ? (
        <SectionToggle type="button" aria-expanded={open} aria-controls={contentId} onClick={() => setOpen(!open)}>
          <SectionTitleBlock>
            <SectionTitle>{section.title}</SectionTitle>
            {appliesTo}
          </SectionTitleBlock>
          {activeCount > 0 && (
            <>
              <ActiveCount aria-hidden="true">{activeCount}</ActiveCount>
              <span style={visuallyHidden}>, {activeCount} active</span>
            </>
          )}
          <ExpandMoreIcon className="chevron" fontSize="small" data-open={open} />
        </SectionToggle>
      ) : (
        <SectionHeading>
          <SectionTitle>{section.title}</SectionTitle>
        </SectionHeading>
      )}
      <Collapse in={open} id={contentId}>
        <SectionBody>{children}</SectionBody>
      </Collapse>
    </Section>
  );
}

// #region Styles

const FilterContainer = styled(Paper, { shouldForwardProp: (prop) => prop !== "dialog" })<{ dialog: boolean }>(
  () => ({
    maxWidth: "initial",
    margin: "10px",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    minHeight: "calc(100% - 20px)",
  }),
);
const Title = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1.5, 2, 0.5),
}));

const Section = styled("section")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  "&:first-of-type": { borderTop: "none" },
}));
const SectionHeading = styled("div")(({ theme }) => ({
  padding: theme.spacing(1, 2, 0.5),
}));
const SectionToggle = styled("button")(({ theme }) => ({
  alignItems: "center",
  background: "none",
  border: "none",
  color: "inherit",
  cursor: "pointer",
  display: "flex",
  font: "inherit",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  textAlign: "left",
  width: "100%",
  "&:hover": { backgroundColor: theme.palette.action.hover },
  "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  "& .chevron": {
    color: theme.palette.text.secondary,
    marginLeft: "auto",
    transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shortest }),
  },
  "& .chevron[data-open='true']": { transform: "rotate(180deg)" },
}));
// The title, and under it the one kind of profile the section narrows, both flush left.
// It takes the spare width, so the active count sits beside the chevron in every header.
const SectionTitleBlock = styled("span")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
}));
const SectionTitle = styled("span")(({ theme }) => ({
  ...theme.typography.subtitle2,
}));
const AppliesTo = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  lineHeight: "16px",
}));
const ActiveCount = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  backgroundColor: theme.palette.primary.main,
  borderRadius: theme.spacing(1.25),
  color: theme.palette.primary.contrastText,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: "20px",
  minWidth: 20,
  padding: theme.spacing(0, 0.75),
  textAlign: "center",
}));
const SectionBody = styled("div")(({ theme }) => ({
  paddingBottom: theme.spacing(1),
}));

// Dense only in the sidebar, which is a mouse's. The same list is the phone's filter dialog, where a
// 28px row is too small a target for a finger.
const FilterRow = styled(ListItem, { shouldForwardProp: (prop) => prop !== "dense" })<{ dense: boolean }>(
  ({ theme, dense }) => ({
    gap: theme.spacing(1),
    ...(dense ? { paddingBottom: 2, paddingTop: 2 } : {}),
  }),
);
const FilterTitle = styled(ListItemText)(() => ({
  margin: 0,
}));
const CheckboxRow = styled("div", { shouldForwardProp: (prop) => prop !== "dense" })<{ dense: boolean }>(
  ({ theme, dense }) => ({
    padding: theme.spacing(0, 2, 0, 1),
    "& .MuiFormControlLabel-root": { marginLeft: 0, marginRight: 0, width: "100%" },
    ...(dense ? { "& .MuiCheckbox-root": { padding: 4 } } : {}),
  }),
);
const ControlRow = styled("div", { shouldForwardProp: (prop) => prop !== "dense" })<{ dense: boolean }>(
  ({ theme, dense }) => ({
    padding: dense ? theme.spacing(0.75, 2) : theme.spacing(1, 2),
  }),
);

// #endregion
